/**
 * Swatches of the generated architecture: walls, windows, doors and floors.
 * Every texel of the architecture atlas is one of these colours or fully
 * transparent. Values are sRGB hex; an eight-digit value carries straight
 * alpha. Both renderers and the pixel palette read them from here.
 */

/** The one outline ink, used by architecture and sprites alike. */
export const TOWN_INK = "#262935";

/** Wall, window and door swatches. */
export const TOWN_WALL_SWATCHES = {
  ink: TOWN_INK,

  // Slate caps and faces.
  cap: "#4b505d",
  capRim: "#6a6f7c",
  slateBase: "#363a46",
  slate: "#474c59",
  slateSeam: "#40454f",
  slateTop: "#6a6f7c",

  // Square posts at joints, ends and jambs.
  post: "#646875",
  postLight: "#80859a",
  postDark: "#3f4350",

  // Plaster on the tall north wall and low plaster faces.
  plaster: "#f4eae0",
  plasterShade: "#e6d9c8",
  baseboard: "#c9b9a6",
  sill: "#e8eef3",

  // Sandstone on the south facade and its piers.
  stone: "#b3a593",
  mortar: "#9a8d7e",
  quoin: "#c9bca9",
  plinth: "#5b5f6b",

  // Stone parapet on the terrace north edge.
  parapetCap: "#5a5f6c",
  parapetJoint: "#52565f",

  // Restroom wall tile.
  tile: "#8aa0c2",
  tileDark: "#7e95ba",
  tileGrout: "#6d84aa",
  tileShine: "#a3b5d1",

  // Wood panelling.
  wood: "#d89b59",
  woodLight: "#e2ab6c",
  woodSeam: "#b97e43",

  // Windows and glass fronts. Painted windows are opaque.
  glass: "#6fc1ec",
  glassStreak: "#b8ecfb",
  glassDeep: "#58b1e3",
  frosted: "#dfeaf2",
  frostedShade: "#c9d6e3",
  navy: "#3e4f8f",
  navyLight: "#8fa0d6",
  silver: "#d5dbe6",
  silverShade: "#9aa3b5",

  // See-through panes. The pane is #6fc1ec at alpha 0.5, the railing infill at 0.35.
  pane: "#6fc1ec80",
  paneDeep: "#58b1e380",
  paneStreak: "#b8ecfbc0",
  railingPane: "#6fc1ec59",

  // Door leaves. Glass leaves reuse the pane and frame swatches.
  leaf: "#59607a",
  leafPanel: "#6f7894",

  // Hedges.
  hedge: "#4f9a4a",
  hedgeDark: "#3f8540",
  hedgeLight: "#66b35a",
  hedgeBase: "#2f6b37",

  // Terrace planters.
  planter: "#606266",
  planterLight: "#6f7076",
  planterRim: "#8a8b90",
  planterLamp: "#ffd98a",
  planterLampCore: "#fff3c8",
  soil: "#6b4a33",
  soilDark: "#573b29",

  // Flowers in hedges, planters, beds and grass.
  flowerPink: "#f4a6c8",
  flowerWhite: "#f6f3e8",
  flowerYellow: "#f5d76e",
} as const;

export type TownWallSwatch = keyof typeof TOWN_WALL_SWATCHES;

/** Painted line on threshold and glass-wall cells. */
export const TOWN_FLOOR_TRACK_COLOR = "#9aa3b1";

/** Floor pattern families. Each family paints its own swatch shape. */
export const TOWN_FLOOR_PATTERNS = [
  "planks",
  "deck",
  "carpet",
  "rug",
  "checker",
  "tile",
  "slabs",
  "pavers",
  "grass",
  "mat",
  "bed",
] as const;
export type TownFloorPattern = (typeof TOWN_FLOOR_PATTERNS)[number];

/** Boards running east-west: four per tile, staggered butt joints. */
export type TownPlankSwatch = Readonly<{ boards: readonly [string, string, string]; seam: string }>;
/** Mottled pile: a base with darker and lighter flecks. */
export type TownPileSwatch = Readonly<{ base: string; dark: string; light: string }>;
/** Square tiles or slabs with a joint line. */
export type TownTileSwatch = Readonly<{ light: string; dark: string; joint: string; shine?: string }>;
/** Running-bond pavers. */
export type TownPaverSwatch = Readonly<{ base: string; dark: string; light: string; joint: string }>;
export type TownGrassSwatch = Readonly<{ base: string; blade: string; light: string; flowers: readonly string[] }>;
export type TownMatSwatch = Readonly<{ base: string; weave: string; chevron: string }>;
export type TownBedSwatch = Readonly<{
  soil: string;
  soilDark: string;
  leaf: string;
  leafDark: string;
  leafLight: string;
  flowers: readonly string[];
}>;

/**
 * Floor swatches by pattern and palette. An area or decal names a pair, for
 * example `{ pattern: "planks", palette: "hall" }`, and the atlas paints one
 * rect per pair.
 */
export const TOWN_FLOOR_SWATCHES = {
  planks: {
    hall: { boards: ["#ebcba7", "#e3c09a", "#d8b38c"], seam: "#c49f78" },
    booth: { boards: ["#dfa463", "#d89b59", "#cd9150"], seam: "#b97e43" },
  },
  deck: {
    teak: { boards: ["#c78650", "#bb7d4d", "#b07345"], seam: "#9a6038" },
  },
  carpet: {
    blue: { base: "#6184c8", dark: "#5677bd", light: "#6f92d2" },
    grey: { base: "#b8bcc8", dark: "#aeb2bf", light: "#c3c7d1" },
    recreation: { base: "#a4a7b0", dark: "#9a9da7", light: "#aeb1b9" },
    aisle: { base: "#8ca2c0", dark: "#8298b7", light: "#97adc9" },
    // Street and car park: asphalt with grit, and the solid paint on it.
    street: { base: "#5b606e", dark: "#535866", light: "#666b79" },
    paint: { base: "#f3f1e7", dark: "#f3f1e7", light: "#f3f1e7" },
    "paint-yellow": { base: "#f5c84a", dark: "#f5c84a", light: "#f5c84a" },
  },
  rug: {
    gold: { base: "#f0b348", dark: "#e5a53c", light: "#f6c15e" },
    sage: { base: "#57a067", dark: "#4f9562", light: "#62ab70" },
    sky: { base: "#5996c2", dark: "#4f8ab7", light: "#66a3cd" },
    beige: { base: "#bea272", dark: "#b49468", light: "#c8ad7e" },
  },
  checker: {
    cream: { light: "#f1dfc9", dark: "#ead2b7", joint: "#e2c9ad" },
  },
  tile: {
    restroom: { light: "#8aa0c2", dark: "#7e95ba", joint: "#6d84aa", shine: "#a3b5d1" },
  },
  slabs: {
    plaza: { light: "#b7bbc5", dark: "#aaafbd", joint: "#9ba1b0" },
  },
  pavers: {
    path: { base: "#92a0b4", dark: "#8794a9", light: "#9dabbe", joint: "#7c899f" },
  },
  grass: {
    meadow: { base: "#8acc6d", blade: "#7fc062", light: "#97d57a", flowers: ["#f4a6c8", "#f6f3e8", "#f5d76e"] },
  },
  mat: {
    green: { base: "#67b275", weave: "#5fa96d", chevron: "#a9dcb2" },
  },
  bed: {
    soil: {
      soil: "#6b4a33",
      soilDark: "#573b29",
      leaf: "#4f9a4a",
      leafDark: "#3f8540",
      leafLight: "#66b35a",
      flowers: ["#f4a6c8", "#f6f3e8", "#f5d76e"],
    },
  },
} as const satisfies {
  planks: Readonly<Record<string, TownPlankSwatch>>;
  deck: Readonly<Record<string, TownPlankSwatch>>;
  carpet: Readonly<Record<string, TownPileSwatch>>;
  rug: Readonly<Record<string, TownPileSwatch>>;
  checker: Readonly<Record<string, TownTileSwatch>>;
  tile: Readonly<Record<string, TownTileSwatch>>;
  slabs: Readonly<Record<string, TownTileSwatch>>;
  pavers: Readonly<Record<string, TownPaverSwatch>>;
  grass: Readonly<Record<string, TownGrassSwatch>>;
  mat: Readonly<Record<string, TownMatSwatch>>;
  bed: Readonly<Record<string, TownBedSwatch>>;
};

/** Palettes available for one pattern. */
export type TownFloorPalette<P extends TownFloorPattern = TownFloorPattern> = P extends TownFloorPattern
  ? keyof (typeof TOWN_FLOOR_SWATCHES)[P] & string
  : never;

/**
 * Edge colours a decal may name in `border`, such as the kerb around paving.
 * The floor painter draws the border inside the decal's edge.
 */
export const TOWN_FLOOR_DECAL_BORDERS = {
  kerb: "#8e9cb1",
  gold: "#e5a53c",
  sage: "#7cc18a",
  sky: "#4f8ab7",
  beige: "#a98a5f",
  mat: "#4f9a5e",
} as const;
export type TownFloorDecalBorder = keyof typeof TOWN_FLOOR_DECAL_BORDERS;

/** Straight-alpha RGBA bytes of a swatch. */
export function townSwatchRgba(swatch: string): readonly [number, number, number, number] {
  const match = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(swatch);
  if (!match?.[1]) throw new Error(`${swatch} is not a #rrggbb or #rrggbbaa swatch.`);
  const rgb = Number.parseInt(match[1], 16);
  const alpha = match[2] ? Number.parseInt(match[2], 16) : 255;
  return [(rgb >> 16) & 255, (rgb >> 8) & 255, rgb & 255, alpha];
}

function collectSwatches(value: unknown, into: Set<string>): Set<string> {
  if (typeof value === "string") into.add(value.toLowerCase());
  else if (Array.isArray(value)) for (const item of value) collectSwatches(item, into);
  else if (value && typeof value === "object") for (const item of Object.values(value)) collectSwatches(item, into);
  return into;
}

/** Every architecture swatch, lowercase. The atlas paints only these and transparent texels. */
export const TOWN_ARCHITECTURE_SWATCH_SET: ReadonlySet<string> = collectSwatches(
  [TOWN_WALL_SWATCHES, TOWN_FLOOR_SWATCHES, TOWN_FLOOR_TRACK_COLOR],
  new Set(),
);
